/**
 * 청구 일정 등록/수정 폼 컴포넌트
 * 청구 일정을 생성하거나 수정하기 위한 폼을 제공하는 컴포넌트입니다.
 *
 * 주요 기능:
 * - 청구 일정 정보 입력
 * - 계약 정보 연동
 * - 청구 금액 설정
 * - 청구 기간 설정
 * - 부가세 자동 계산
 * - 데이터 유효성 검증
 */

import _ from "lodash";
import qs from "qs";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useLocation, useParams } from "react-router-dom";
import { BillingDetail, ScheduleRoundNOverdueItem } from "src/api/billingSchedule/billing-type";
import { getScheduleDetail_RCB01, getScheduleRoundNOverdueList } from "src/api/billingSchedule/billingSchedule-api";
import { DetailTotalData, disabledEdit } from "src/api/contract/contract-types";
import { useApiOperation } from "src/api/hooks";
import { ContentsTitle } from "src/components";
import MetaTag from "src/components/layout/MetaTag";
import useNavigate from "src/hooks/usePartnerNavigate";
import PagePath from "src/pagePath.json";
import useContractApi from "src/pages/contract/hooks/useContractApi";
import ScheduleBasicInfo from "../components/basicInfo/ScheduleBasicInfo";
import ScheduleInfo from "./components/billingScheduleInfo/ScheduleInfo";
import MntBillingForm from "./components/mntBillingForm/MntBillingForm";

const tabs = [
  { value: "basicInfo", label: "기본정보" },
  { value: "schedule", label: "이용료 빌링 현황" },
  { value: "mnt", label: "관리비 빌링 현황" },
];

/**
 * 청구 일정 등록/수정 폼 컴포넌트
 * 청구 일정의 생성 및 수정을 위한 폼을 제공합니다.
 *
 * @param id - 라우트 파라미터. 청구 일정 id 가 아니라 계약 id 다 (빌링 스케줄 상세는 계약 단위 화면)
 * @returns 청구 일정 등록/수정 폼 화면
 */
const BillingScheduleDetail = () => {
  const { getContractDetail } = useContractApi();

  const navigate = useNavigate();
  const location = useLocation();
  const { id } = useParams();
  const [scheduleId, setScheduleId] = useState(0);

  const [roundList, setRoundList] = useState<ScheduleRoundNOverdueItem[]>([]);
  const [overdueList, setOverdueList] = useState<any>([]);

  const [contractDetail, setContractDetail] = useState<DetailTotalData>();

  const [spaceProductType, setSpaceProductType] = useState("");

  const [editable, setEditable] = useState(false);

  const { executeAsync: getRoundList } = useApiOperation(getScheduleRoundNOverdueList);
  const { executeAsync: getScheduleList } = useApiOperation(getScheduleDetail_RCB01);

  // location search (url query parameter) 를 읽어서 object 로 변환
  const queryParams = useMemo(
    () =>
      qs.parse(location.search, {
        ignoreQueryPrefix: true,
        allowDots: true,
      }),
    [location],
  );

  // 현재 활성화 되어야하는 tab
  const activeTab = useMemo(() => {
    if (queryParams?.tab) {
      return tabs.find((tab) => tab.value === queryParams?.tab);
    }
    return tabs[0];
  }, [queryParams]);

  // 탭 클릭시 callback 함수
  const clickTab = useCallback(
    (tab: { value: string; label: string }) => {
      // console.log("clickTab", tab);
      const newQueryParams = { ...queryParams };
      if (newQueryParams?.tab) {
        delete newQueryParams.tab;
      }

      let path = location.pathname;
      newQueryParams.tab = tab.value;
      if (tab.value === "basicInfo") {
        path = PagePath.billing.detail.replace(":id", String(id));
      }
      // console.log("location.pathname", location, id, newQueryParams);
      // query parameter 에 tab 추가
      const newQueryParamStr = qs.stringify(newQueryParams, { allowDots: true });
      navigate(path + "?" + decodeURIComponent(newQueryParamStr));
    },
    [navigate, location, queryParams],
  );

  const breadCrumbs = [{ value: "billing", label: "빌링스케줄 상세" }];
  if (activeTab) {
    // 마지막 bread crumb 는 현재 선택된 탭
    breadCrumbs.push({
      value: activeTab.value,
      label: activeTab.label,
    });
  }

  const [stopObj, setStopObj] = useState<BillingDetail | undefined>();

  const callList = async () => {
    if (id && Number(id) !== 0) {
      // setRoundList([]);
      // setOverdueList([]);
      //
      const res = await getScheduleList({ contractId: Number(id), supplyType: "RENTAL" });

      // 저장 뒤 재조회하는 곳(MakeBillPopup, 즉시결제)이 이어서 안내를 띄우므로 throw 하지 않음
      const response = await getRoundList({ scheduleId: Number(res!.data!.data!.scheduleList![0]!.scheduleId!) }).catch(() => undefined);

      if (response) {
        const sorted = _.sortBy(response.data.data.content, ["bill.billOrder"]);

        const normal = sorted.filter((sort) => {
          if (sort.bill.isOverdueBill === false) {
            return sort;
          }
        });
        const overdues = sorted.filter((sort) => {
          if (sort.bill.isOverdueBill === true) {
            return sort;
          }
        });

        setRoundList(normal);
        setOverdueList(overdues);
      }
      //
    }
  };

  const fetchBillDate = async () => {
    //call contractDetail
    if (!id) return;
    const contractDetailResponse = await getContractDetail(String(id));
    setContractDetail(contractDetailResponse);

    if (disabledEdit.includes(contractDetailResponse?.contract?.contractStep!)) {
      setEditable(true);
    }
    setSpaceProductType(contractDetailResponse.product.productName);

    const sd = contractDetailResponse.scheduleList!.find((schedule) => schedule.supplyType === "RENTAL");

    if (sd?.scheduleId) setScheduleId(Number(sd.scheduleId));
    setStopObj(sd);
    await callList();

    // }
  };

  useEffect(() => {
    fetchBillDate();
  }, []);

  // title 에 건물명 바인딩
  let contractApplyNumber;
  if (contractDetail && contractDetail.contractManage?.contractApplyNumber) {
    contractApplyNumber = contractDetail.contractManage?.contractApplyNumber;
  }

  const checkTabs = useMemo(() => {
    if (
      contractDetail &&
      (contractDetail?.contract?.isMntContain! ||
        (contractDetail.product && contractDetail.product.productType === "FULL_COURT" && contractDetail.product.productType === "OPEN_COURT"))
    ) {
      return tabs.filter((tab: any) => tab.value !== "mnt");
    }
    return tabs;
  }, [contractDetail]);

  return (
    <>
      <MetaTag subTitle={contractApplyNumber} />

      <div className="page-contract-detail">
        <ContentsTitle
          title="빌링스케줄 상세"
          tabs={checkTabs}
          activeTab={activeTab}
          clickTab={clickTab}
          detailInfo={[
            // 목록의 "빌링 스케줄 ID" 컬럼과 같은 값(이용료 스케줄 id)을 쓴다.
            // 계약 ID / 신청 번호는 기본정보 탭의 신청/계약 정보에서 확인한다.
            { title: "빌링 스케줄 ID", value: scheduleId ? String(scheduleId) : "-" },
          ]}
        />

        {activeTab?.value === "basicInfo" && <ScheduleBasicInfo contractDetail={contractDetail} />}
        {/* 수정페이지 */}
        {activeTab?.value === "schedule" && (
          <ScheduleInfo
            callList={callList}
            contractBaseInfo={contractDetail}
            roundList={roundList}
            overdueList={overdueList}
            spaceProductType={spaceProductType}
            stopObj={stopObj}
            setStopObj={setStopObj}
            editable={editable}
            scheduleId={scheduleId}
          />
        )}

        {activeTab?.value === "mnt" && (
          <MntBillingForm
            basicInfo={contractDetail}
            callList={callList}
            contractBaseInfo={contractDetail}
            spaceProductType={spaceProductType}
            stopObj={stopObj}
            editable={editable}
          />
        )}
      </div>
    </>
  );
};

export default BillingScheduleDetail;
